// Remembers the page the user was on when their session ended, so that after
// the Keycloak round trip (full page loads, in the same tab) they land back
// there instead of on the default page.
//
// sessionStorage, not the OIDC `state` param: the value has to be captured
// before the login redirect even starts (a 401 calls logout(), which leaves
// the app via signoutRedirect), and it must survive landing on /login and
// clicking "Sign in" later. It is per-tab, so it never leaks between tabs.
const RETURN_TO_KEY = 'auth.returnTo';

// "/login", "/login?code=..." and "/login#..." but not "/login-history".
const LOGIN_PATH_PATTERN = /^\/login(?:[?#]|$)/;

// Only absolute in-app paths. "//host" is protocol-relative, i.e. another
// origin, and the pathname of a crafted link can start with it.
const isRememberablePath = (path) =>
  typeof path === 'string' &&
  path.startsWith('/') &&
  !path.startsWith('//') &&
  path !== '/' &&
  !LOGIN_PATH_PATTERN.test(path);

// Deliberately in memory, not sessionStorage: it must die with the page that
// signoutRedirect() is about to unload, so the next visit starts clean.
let isSuspended = false;

// `path` is router-relative and may include the query string and hash, e.g.
// "/locations/168/dashboard?tab=2". Anything not worth returning to (login
// page, root, unsafe values) is ignored and leaves the current value alone,
// which also keeps repeated logout() calls from overwriting a good path with
// "/login" once the router has already moved there.
export const rememberReturnTo = (path) => {
  if (!isSuspended && isRememberablePath(path)) {
    sessionStorage.setItem(RETURN_TO_KEY, path);
  }
};

// Non-destructive so it is safe to call while rendering.
export const getReturnTo = () => sessionStorage.getItem(RETURN_TO_KEY);

export const clearReturnTo = () => {
  sessionStorage.removeItem(RETURN_TO_KEY);
};

// For a deliberate sign-out: drops the stored path and stops anything from
// storing a new one. Clearing alone isn't enough - setting isLoggedIn to false
// makes the router bounce to /login (which remembers the page being left)
// before the browser has actually navigated away to Keycloak.
export const suspendReturnTo = () => {
  clearReturnTo();
  isSuspended = true;
};
